import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_CONFIG } from "../config/recaptcha";
import "../styles/AppointmentRequestModal.css";

const AppointmentRequestModal = ({ isOpen, onClose, doctorId, doctorName, doctorEmail }) => {
  const [patientName, setPatientName] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleAppointmentRequest = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      setStatus("Please complete the reCAPTCHA verification.");
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    
    try {
      // Use doctorId prop for the API call
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/${doctorId}/request-appointment/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: patientName,
          email: patientEmail,
          phone: phoneNumber,
          date: appointmentDate,
          time: appointmentTime,
          reason: reason,
          recaptcha_token: captchaValue,
        }),
      });
      
      const data = await response.json();
      
      if (data.status) {
        setStatus("Appointment request sent successfully! The doctor will contact you soon.");
        setPatientName("");
        setPatientEmail("");
        setPhoneNumber("");
        setAppointmentDate("");
        setAppointmentTime("");
        setReason("");
        setCaptchaValue(null);
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        // Close modal after successful request
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setStatus(data.message || "Failed to send appointment request.");
        // Reset reCAPTCHA on failure
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaValue(null);
      }
    } catch (error) {
      setStatus("Error sending appointment request. Please try again.");
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaValue(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus(null);
    setPatientName("");
    setPatientEmail("");
    setPhoneNumber("");
    setAppointmentDate("");
    setAppointmentTime("");
    setReason("");
    setCaptchaValue(null);
    setIsSubmitting(false);
    // Reset reCAPTCHA
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    // Clear any previous error messages when user completes reCAPTCHA
    if (value && status && status.includes("reCAPTCHA")) {
      setStatus(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="appointment-request-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Request an Appointment</h2>
        <p>
          Request an appointment with {doctorName ? doctorName : "this practitioner"}. Fill out your information and we'll send your request to the doctor. We will contant you when the doctor confirms the appointment.
        </p>
        <form onSubmit={handleAppointmentRequest}>
          <input
            type="text"
            placeholder="Your Full Name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <input
            type="email"
            placeholder="Your Email"
            value={patientEmail}
            onChange={(e) => setPatientEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <input
            type="tel"
            placeholder="Your Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <div className="appointment-date-time-fields">
            <div className="appointment-date-time-combined" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={appointmentDate}
                  onChange={e => {
                    let val = e.target.value.replace(/[^0-9\/\-]/g, '');
                    // Accepts 8 digits, with or without / or -
                    // Normalize to DD/MM/YYYY
                    let normalized = val;
                    // Remove all non-digits for parsing
                    const digits = val.replace(/[^\d]/g, '');
                    if (digits.length === 8) {
                      // Try to format as DD/MM/YYYY
                      normalized = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4,8)}`;
                    }
                    setAppointmentDate(normalized);
                  }}
                  required
                  disabled={isSubmitting}
                  inputMode="numeric"
                  pattern="\d{2}/\d{2}/\d{4}"
                  maxLength={10}
                  style={{ paddingRight: "60px" }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  style={{
                    position: "absolute",
                    right: "6px",
                    background: "none",
                    border: "none",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => {
                    if (!isSubmitting) {
                      document.getElementById("hidden-date-picker")?.showPicker?.();
                    }
                  }}
                  disabled={isSubmitting}
                  aria-label="Pick date"
                >
                  <svg width="22" height="22" fill="none" stroke="#888" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="#888"/>
                    <path d="M16 2v4M8 2v4M3 10h18" stroke="#888"/>
                  </svg>
                </button>
          <input
                  id="hidden-date-picker"
            type="date"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    opacity: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none"
                  }}
                  tabIndex={-1}
                  value={
                    /^\d{2}\/\d{2}\/\d{4}$/.test(appointmentDate)
                      ? `${appointmentDate.slice(6,10)}-${appointmentDate.slice(3,5)}-${appointmentDate.slice(0,2)}`
                      : ""
                  }
                  onChange={e => {
                    // Convert YYYY-MM-DD to DD/MM/YYYY
                    const val = e.target.value;
                    if (val) {
                      const [year, month, day] = val.split("-");
                      setAppointmentDate(`${day}/${month}/${year}`);
                    } else {
                      setAppointmentDate("");
                    }
                  }}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="HH:MM AM/PM"
                  value={appointmentTime}
                  onChange={e => {
                    let val = e.target.value.toUpperCase();
                    // Remove invalid characters, allow only digits, colon, space, A, P, M
                    val = val.replace(/[^0-9:APM ]/gi, '');

                    // Auto-format to HH:MM AM/PM as user types
                    // Remove extra spaces
                    val = val.replace(/\s+/g, ' ').trim();

                    // If user types 4 digits, auto-insert colon
                    const digits = val.replace(/[^\d]/g, '');
                    if (digits.length === 4 && !val.includes(':')) {
                      val = `${digits.slice(0,2)}:${digits.slice(2,4)}`;
                    }

                    // If user types 5 digits (e.g. 12345), ignore extra
                    if (digits.length > 4) {
                      val = val.slice(0, 5);
                    }

                    // If user types AM/PM without space, add space
                    val = val.replace(/(\d{1,2}:\d{2})([AP]M)/, '$1 $2');

                    // Only allow AM or PM at the end
                    val = val.replace(/(AM|PM).*$/i, (match) => match.slice(0,2).toUpperCase() === 'AM' ? 'AM' : 'PM');

                    setAppointmentTime(val);
                  }}
            required
            disabled={isSubmitting}
                  inputMode="text"
                  pattern="^(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$"
                  maxLength={8}
                  style={{ paddingRight: "60px", textAlign: "justify" }}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  style={{
                    position: "absolute",
                    right: "6px",
                    background: "none",
                    border: "none",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => {
                    if (!isSubmitting) {
                      document.getElementById("hidden-time-picker")?.showPicker?.();
                    }
                  }}
                  disabled={isSubmitting}
                  aria-label="Pick time"
                >
                  <svg width="22" height="22" fill="none" stroke="#888" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="#888" fill="none"/>
                    <path d="M12 6v6l4 2" stroke="#888"/>
                  </svg>
                </button>
          <input
                  id="hidden-time-picker"
            type="time"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    opacity: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none"
                  }}
                  tabIndex={-1}
                  value={
                    // Convert appointmentTime (HH:MM AM/PM) to 24-hour format for input[type="time"]
                    (() => {
                      const match = appointmentTime.match(/^(\d{1,2}):(\d{2}) ?(AM|PM)$/i);
                      if (match) {
                        let [_, hour, minute, period] = match;
                        hour = parseInt(hour, 10);
                        if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
                        if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
                        return `${hour.toString().padStart(2, '0')}:${minute}`;
                      }
                      return "";
                    })()
                  }
                  onChange={e => {
                    // Convert 24-hour time to HH:MM AM/PM
                    const val = e.target.value;
                    if (val) {
                      let [hour, minute] = val.split(":");
                      hour = parseInt(hour, 10);
                      let period = "AM";
                      if (hour === 0) {
                        hour = 12;
                        period = "AM";
                      } else if (hour === 12) {
                        period = "PM";
                      } else if (hour > 12) {
                        hour = hour - 12;
                        period = "PM";
                      }
                      setAppointmentTime(`${hour.toString().padStart(2, '0')}:${minute} ${period}`);
                    } else {
                      setAppointmentTime("");
                    }
                  }}
            required
            disabled={isSubmitting}
          />
              </div>
            </div>
          </div>
          <textarea
            placeholder="Reason for appointment"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            disabled={isSubmitting}
          />
          
          {/* reCAPTCHA */}
          <div className="recaptcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_CONFIG.SITE_KEY}
              onChange={handleCaptchaChange}
              theme="light"
              size="normal"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={isSubmitting || !captchaValue}
            className={isSubmitting || !captchaValue ? "submit-btn-disabled" : ""}
          >
            {isSubmitting ? "Sending Request..." : "Send Appointment Request"}
          </button>
        </form>
        
        {status && (
          <div className={`status-message ${status.includes("success") ? "success" : "error"}`}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentRequestModal; 